/* Skin-Nomad live prices inside blog article bodies
 * ---------------------------------------------------
 * Shopify does not evaluate Liquid inside `article.content`, so a price written
 * into a blog post is a dead string that goes stale the moment anything changes.
 * This script hydrates it from the live product instead.
 *
 * Loaded from layout/theme.liquid on article pages only.
 *
 * In any article body, write:
 *   <span class="sn-price" data-product="beauty-of-joseon-relief-sun"></span>
 *
 * Behaviour:
 *   - reads the live price from the CURRENT MARKET's product endpoint
 *   - formats it in the CURRENT MARKET's currency, symbol and number style
 *   - renders "24.00", or "from 24.00" when variants differ in price
 *   - renders "24.00 (was 30.00)" when the product is on sale
 *   - if the product is missing, unlisted or the fetch fails, the span is
 *     removed entirely rather than printing an error. The sentence around it
 *     must therefore still read correctly with the price absent.
 *
 * Markets, updated 22 August 2026
 * ------------------------------
 * The store sells into GBP, EUR, CAD and AUD markets, two of which have buyer
 * local currencies switched on, and France runs on its own domain. So neither
 * the currency nor the amount can be assumed from the URL.
 *
 * Two things make this work:
 *
 *   1. The fetch goes through `Shopify.routes.root`, which is "/" on the UK
 *      store, "/en-eu/" on a subfolder market and "/" again on skin-nomad.fr.
 *      Requesting the product through that prefix returns the price already
 *      converted into the market's presentment currency. Fetching a bare
 *      "/products/x.js" always returns GBP, whatever market the reader is in.
 *
 *   2. Formatting uses `Shopify.currency.active`, the live presentment currency
 *      code, through Intl.NumberFormat with the page's own locale. That gets the
 *      symbol, its position and the separators right: "24,00 €" in France,
 *      "$24.00" in Canada, "£24.00" at home. Intl also handles currencies with
 *      no decimal places on its own, which a hardcoded toFixed(2) does not.
 *
 * The previous version hardcoded "£" and leaned on the currency converter app to
 * swap it, which converted the number and left the pound sign in place. There is
 * deliberately no `.transcy-money` wrapper here any more: the figure this script
 * prints is already in the reader's currency, and a converter running over the
 * top of it would convert a converted number.
 */
(function () {
  'use strict';

  var NODES = document.querySelectorAll('.sn-price[data-product]');
  if (!NODES.length) return;

  var S = window.Shopify || {};
  var ROOT = (S.routes && S.routes.root) || '/';
  var CURRENCY = (S.currency && S.currency.active) || 'GBP';
  var LOCALE = document.documentElement.lang || 'en-GB';

  var formatter = null;
  try {
    formatter = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY });
  } catch (e) {
    formatter = null;
  }

  var money = function (minorUnits) {
    var amount = minorUnits / 100;
    if (formatter) return formatter.format(amount);
    return CURRENCY + ' ' + amount.toFixed(2);
  };

  var cache = {};

  var render = function (node, product) {
    var prices = (product.variants || [])
      .filter(function (v) { return v.available; })
      .map(function (v) { return v.price; });

    if (!prices.length) {
      prices = (product.variants || []).map(function (v) { return v.price; });
    }
    if (!prices.length) { node.remove(); return; }

    var low = Math.min.apply(null, prices);
    var high = Math.max.apply(null, prices);

    var out = (low !== high ? 'from ' : '') + money(low);

    var compare = product.compare_at_price_min || 0;
    if (low === high && compare > low) {
      out += ' (was ' + money(compare) + ')';
    }

    node.textContent = out;
    node.setAttribute('data-hydrated', 'true');
    node.setAttribute('data-currency', CURRENCY);
  };

  Array.prototype.forEach.call(NODES, function (node) {
    var handle = node.getAttribute('data-product');
    if (!handle) { node.remove(); return; }

    if (cache[handle]) {
      cache[handle].then(function (p) { render(node, p); }).catch(function () { node.remove(); });
      return;
    }

    // encodeURIComponent so handles containing non-ASCII characters resolve.
    // The NINE LESS handle contains a trademark sign and 404s without this.
    cache[handle] = fetch(ROOT + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('status ' + r.status);
        return r.json();
      });

    cache[handle]
      .then(function (p) { render(node, p); })
      .catch(function () { node.remove(); });
  });
})();
